import React, { useState, useEffect } from "react";
import { ReactReader } from "react-reader";
import { Button, Image, Card, Spin, message } from "antd";
import { AssetPreviewTypes } from "common/type";
import { assetPreview } from "utils";
import "./asset_preview.scss";
import epub from "assets/images/filetypes/epub.svg";
import EpubViewer from "../../components/EpubViewer";
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import "@cyntler/react-doc-viewer/dist/index.css";
import { SERVER_BASE_URL } from "common/constants";
import { DownloadOutlined } from "@ant-design/icons";
import { getCenterShort, downloadAsset } from "utils";

/**
 * @description Header Component
 * @param {IComponent} props
 * @returns {React.ReactElement}
 */

type PropsType = {
  asset: any;
};

const AssetPreview: React.FC<PropsType> = (
  props: PropsType
): React.ReactElement => {
  const { asset } = props;
  const [loading, setLoading] = useState(true);
  const [location, setLocation] = useState<any>(undefined);
  const [downloading, setDownloading] = useState(false);

  const handleLocationChanged = (newLocation: string) => {
    setLoading(false); // Assume the book is loaded once a location is set
    setLocation(newLocation);
  };

  // const downloadLargeImage = async () => {
  //   if (!asset?.url) {
  //     message.error("No URL found for asset");
  //     return;
  //   }

  //   setDownloading(true);

  //   try {
  //     message.success("Download started");

  //     const response = await fetch(asset.url, {
  //       mode: "cors",
  //       headers: {
  //         'Accept': 'image/*'
  //       }
  //     });

  //     if (!response.ok) {
  //       throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  //     }

  //     const blob = await response.blob();
  //     const objectUrl = window.URL.createObjectURL(blob);

  //     const link = document.createElement("a");
  //     link.href = objectUrl;
  //     link.download = asset?.name || `image_${Date.now()}`;
  //     document.body.appendChild(link);
  //     link.click();

  //     // Cleanup
  //     document.body.removeChild(link);
  //     setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);

  //   } catch (err) {
  //     window.open(asset.url, '_blank');
  //     message.info("Opened in new tab - you can right-click to save");
  //   } finally {
  //     setDownloading(false);
  //     message.success("Download completed");
  //   }
  // };

  const downloadLargeImage = async () => {
    if (!asset?.url) {
      message.error("No URL found for asset");
      return;
    }

    setDownloading(true);

    try {
      message.success("Download started");

      await downloadAsset(
        asset.url,
        asset?.mimetype || "application/octet-stream",
        asset?.name || `file_${Date.now()}`
      );

      message.success("Download completed");
    } catch (err) {
      console.error("Download failed:", err);
      window.open(asset.url, "_blank");
      message.info("Opened in new tab - you can right-click to save");
    } finally {
      setDownloading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="asset-preview">
      {(() => {
        const type = asset?.mimetype?.split("/")[0];
        console.log('type', type);
        console.log('asset', asset)
        switch (type) {
          case "image":
            if (asset?.mimetype === "image/heic" || asset?.mimetype === "image/heif") {
              return <h1 style={{ color: "#ffffff" }}>No Preview Available</h1>;
            }
            const isLarge = asset?.size && asset.size > 5 * 1024 * 1024; // 5MB

            if (isLarge) {
              return (
                <div style={{
                  textAlign: "center",
                  color: "#fff",
                  padding: "20px 20px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "20px"
                }}>
                  <div style={{ fontSize: "48px", opacity: 0.6 }}>🖼️</div>
                  <h3 style={{ color: "#fff", margin: 0 }}>Large Image File</h3>
                  <p style={{ margin: 0, opacity: 0.8 }}>
                    Preview not available for large files ({asset?.size ? formatFileSize(asset.size) : '>5MB'}).
                  </p>
                  <Button
                    type="primary"
                    size="large"
                    icon={<DownloadOutlined />}
                    loading={downloading}
                    onClick={downloadLargeImage}
                    style={{
                      minWidth: "160px",
                      height: "40px"
                    }}
                  >
                    {downloading ? "Downloading..." : "Download Image"}
                  </Button>
                </div>
              );
            }

            return (
              <Image
                width={"auto"}
                height="auto"
                className="img-height"
                src={assetPreview(asset?.url, asset?.mimetype)}
                preview={false}
              />
            );
          case "video":
            return (
              <video
                width="100%"
                max-height="500px"
                controls
                src={asset?.url}
                style={{ maxHeight: "500px" }}
              >
                <source src={asset?.url} />
              </video>
            );
          case "application":
            if (asset?.mimetype === "application/octet-stream") {
              return <h1 style={{ color: "#ffffff" }}>No Preview Available</h1>;
            }
            if (asset?.mimetype) {
              // Handle .e.pub files
              return (
                <>
                  <div style={{ width: "100%", height: "100%" }}>
                    {asset?.mimetype === "application/pdf" ? (
                      <iframe
                        src={`${SERVER_BASE_URL}/api/asset/preview?url=${asset?.url}`}
                        style={{ width: "100%", height: "400px", border: "none" }}
                      />
                    ) : asset?.mimetype === "application/epub" ||
                      asset?.mimetype === "application/epub+zip" ||
                      asset?.mimetype === "application/vnd.ms-powerpoint" ||
                      asset?.mimetype ===
                      "application/vnd.openxmlformats-officedocument.presentationml.presentation" ? (
                      <div
                        style={{
                          textAlign: "center",
                          color: "#fff",
                          padding: "20px 20px",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: "20px",
                        }}
                      >
                        <div style={{ fontSize: "48px", opacity: 0.6 }}>
                          {asset?.mimetype?.includes("epub") ? "📚" : "📊"}
                        </div>
                        <h3 style={{ color: "#fff", margin: 0 }}>
                          {asset?.mimetype?.includes("epub") ? "Epub File" : "PowerPoint File"}
                        </h3>
                        <p style={{ margin: 0, opacity: 0.8 }}>
                          Preview not available for{" "}
                          {asset?.mimetype?.includes("epub") ? "epub" : "ppt"} files.
                        </p>
                        <Button
                          type="primary"
                          size="large"
                          icon={<DownloadOutlined />}
                          loading={downloading}
                          onClick={downloadLargeImage}
                          style={{ minWidth: "160px", height: "40px" }}
                        >
                          {downloading ? "Downloading..." : "Download File"}
                        </Button>
                      </div>
                    ) : (
                      <Card style={{ width: "100%", height: "400px", overflow: "auto" }}>
                        <DocViewer
                          documents={[
                            { uri: `${SERVER_BASE_URL}/api/asset/preview?url=${asset?.url}` },
                          ]}
                          pluginRenderers={DocViewerRenderers}
                        />
                      </Card>
                    )}
                  </div>

                </>
              );
            }
            return <h1>No Preview available</h1>;
          case "audio":
            return (
              <audio controls className="audio audio_player" src={asset?.url}>
                <source src={asset?.url} />
              </audio>
            );
          case "text":
            return (
              <Image
                width={"100%"}
                height="300px"
                src={assetPreview(asset?.url, asset?.mimetype)}
                preview={false}
              />
            );
          default:
            return <h1>{type}</h1>;
        }
      })()}
    </div>
  );
};

export default AssetPreview;
